import axios from 'axios';
import config from '../config';
import { applySourceSystemHeader } from '../utils/sourceSystemHeaders';

// Auth API endpoint - authentication-framework service on port 8084
const AUTH_API_BASE_URL = (() => {
  const base = config.VITE_AUTH_API_BASE_URL;
  if (!base) {
    throw new Error('VITE_AUTH_API_BASE_URL must be set for authentication API calls');
  }
  return base.replace(/\/$/, '');
})();

const CORE_API_BASE_URL = (() => {
  const base = config.VITE_CORE_API_BASE_URL || config.VITE_API_BASE_URL;
  if (!base) {
    throw new Error('VITE_CORE_API_BASE_URL (or VITE_API_BASE_URL) must be set for core API calls');
  }
  return base.replace(/\/$/, '');
})();

// Create dedicated axios instance for auth
export const authApi = axios.create({
  baseURL: AUTH_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    sourceSystem: 'CUSTOMER_PORTAL',
  },
});

const coreApi = axios.create({
  baseURL: CORE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    sourceSystem: 'CUSTOMER_PORTAL',
  },
});

// Add request interceptor to include JWT token if available
authApi.interceptors.request.use(
  (config) => {
    applySourceSystemHeader(config.headers as unknown as Record<string, unknown>);
    // Always send token if available - backend will decide if it's needed
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  userName: string;     // User's full name for display
  tenantName: string;   // Organization name for display
  role: string;
  profile?: string;
  passwordMustChange?: boolean;
  authProvider?: string;  // 'email' or 'google'
  // Agency admin fields (populated only when profile = 'agency-admin')
  agencyCode?: string;
  agencyLogoUrl?: string;   // Agency logo URL - populated when agencyCode is set
  agencyName?: string;      // Agency display name - populated when agencyCode is set
  allowedTenants?: { id: string; name: string }[];
  // Proxy fields (populated after tenant switch, null on initial login)
  proxyUserId?: string;      // Acting-as user ID (for future user impersonation)
  proxyTenantId?: string;    // Acting-as tenant ID (current context after switch)
  proxyTenantName?: string;  // Acting-as tenant name
}

export interface User {
  id: string;
  email: string;
  name: string;
  tenantId: string;       // Home tenant ID (immutable, for audit trail)
  tenantName: string;     // Home tenant name
  role: string;
  profile?: string;
  agencyCode?: string;
  agencyLogoUrl?: string;   // Agency logo URL - populated at login when agencyCode is set
  agencyName?: string;      // Agency display name - populated at login when agencyCode is set
  allowedTenants?: { id: string; name: string }[];
  // Proxy fields (null before tenant switch, populated after)
  proxyUserId?: string;      // Acting-as user ID
  proxyTenantId?: string;    // Acting-as tenant ID (current context)
  proxyTenantName?: string;  // Acting-as tenant name
}

/**
 * Decode JWT token to extract user info (userId, email, tenantId are in the token)
 */
export const decodeToken = (token: string): { userId: string; email: string; tenantId: string; profile?: string; role?: string } => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      userId: payload.userId || payload.sub,
      email: payload.email,
      tenantId: payload.tenantId,
      profile: payload.profile,
      role: payload.role,
    };
  } catch {
    return { userId: '', email: '', tenantId: '' };
  }
};

/**
 * Login with email and password
 */
export const login = async (email: string, password: string): Promise<{ user: User; token: string; refreshToken: string; passwordMustChange: boolean; authProvider: string }> => {
  const response = await authApi.post<AuthResponse>(
    `/login`,
    { email, password }
  );

  const data = response.data;
  const tokenData = decodeToken(data.accessToken);

  const user: User = {
    id: tokenData.userId,
    email: tokenData.email,
    name: data.userName,
    tenantId: tokenData.tenantId,
    tenantName: data.tenantName,
    role: data.role,
    profile: data.profile,
    agencyCode: data.agencyCode,
    agencyLogoUrl: data.agencyLogoUrl,
    agencyName: data.agencyName,
    allowedTenants: data.allowedTenants,
    // Proxy fields (null on initial login, populated after tenant switch)
    proxyUserId: data.proxyUserId,
    proxyTenantId: data.proxyTenantId,
    proxyTenantName: data.proxyTenantName,
  };

  return {
    user,
    token: data.accessToken,
    refreshToken: data.refreshToken,
    passwordMustChange: data.passwordMustChange ?? false,
    authProvider: data.authProvider ?? 'email',
  };
};

/**
 * Simple registration response - user will login separately
 */
export interface RegisterResponse {
  success: boolean;
  message: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface PublicOnboardingResult {
  organizationId: string;
  primaryCustomerEmail: string;
  additionalCustomerEmail?: string;
  message: string;
}

export interface PublicRegisterRequest {
  email: string;
  fullName: string;
  organizationName: string;
  planTier: string;
  managedBy?: 'SELF' | 'AGENCY';
  sites: Array<{
    siteName: string;
    siteUrl: string;
    sitemapUrl: string;
    siteTier: string;
  }>;
  additionalCustomerEmail?: string;
}

/**
 * Register a new user
 * Returns simple success/failure - user will login separately after registration
 */
export const register = async (
  request: PublicRegisterRequest
): Promise<RegisterResponse> => {
  const response = await coreApi.post<ApiResponse<PublicOnboardingResult>>(
    `/public/organizations/onboarding`,
    {
      organizationName: request.organizationName,
      planTier: request.planTier,
      managedBy: request.managedBy || 'SELF',
      primaryCustomerName: request.fullName,
      primaryCustomerEmail: request.email,
      additionalCustomerEmail: request.additionalCustomerEmail,
      sites: request.sites,
    }
  );

  if (!response.data.success) {
    return {
      success: false,
      message: response.data.error || 'Registration failed',
    };
  }

  return {
    success: true,
    message: response.data.data?.message || 'Registration successful',
  };
};

/**
 * Refresh access token
 */
export const refreshToken = async (refreshTokenValue: string): Promise<{ token: string; refreshToken: string }> => {
  const response = await authApi.post<AuthResponse>(
    `/refresh`,
    { refreshToken: refreshTokenValue }
  );

  return {
    token: response.data.accessToken,
    refreshToken: response.data.refreshToken,
  };
};

/**
 * Get Google OAuth authorization URL
 */
export const getGoogleAuthUrl = async (redirectUri: string): Promise<string> => {
  const response = await authApi.get<{ authorizationUrl: string }>(
    `/oauth/google`,
    { params: { redirectUri } }
  );
  return response.data.authorizationUrl;
};

/**
 * Handle OAuth callback
 */
export const handleOAuthCallback = async (
  code: string,
  redirectUri: string,
  state?: string
): Promise<{ user: User; token: string; refreshToken: string }> => {
  const response = await authApi.post<AuthResponse>(
    `/oauth/callback`,
    { code, redirectUri, state }
  );

  const data = response.data;
  const tokenData = decodeToken(data.accessToken);

  const user: User = {
    id: tokenData.userId,
    email: tokenData.email,
    name: data.userName || tokenData.email.split('@')[0],  // display name from response
    tenantId: tokenData.tenantId,
    tenantName: data.tenantName,
    role: data.role,
  };

  return {
    user,
    token: data.accessToken,
    refreshToken: data.refreshToken,
  };
};

/**
 * Logout
 */
export const logout = async (refreshTokenValue: string): Promise<void> => {
  await authApi.post(`/logout`, { refreshToken: refreshTokenValue });
};

/**
 * Change password (authenticated user)
 */
export const changePassword = async (currentPassword: string, newPassword: string): Promise<{ message: string }> => {
  const response = await authApi.post<{ message: string }>(`/change-password`, {
    currentPassword,
    newPassword,
  });
  return response.data;
};

/**
 * Force change password (after temp password login)
 */
export const forceChangePassword = async (newPassword: string): Promise<{ message: string }> => {
  const response = await authApi.post<{ message: string }>(`/force-change-password`, {
    newPassword,
  });
  return response.data;
};

/**
 * Forgot password (public - sends temp password via email)
 */
export const forgotPassword = async (email: string): Promise<{ message: string }> => {
  const response = await authApi.post<{ message: string }>(`/forgot-password`, { email });
  return response.data;
};

/**
 * Reset password using temp password (public)
 */
export const resetPassword = async (email: string, tempPassword: string, newPassword: string): Promise<{ message: string }> => {
  const response = await authApi.post<{ message: string }>(`/reset-password`, {
    email,
    tempPassword,
    newPassword,
  });
  return response.data;
};
